/**
 * Shared heuristic for spotting an artifact body that is really a full HTML
 * document even though its stored `kind` says otherwise.
 *
 * The model occasionally authors an HTML page (dashboard, invoice, email) but
 * leaves the artifact `kind` at the `create_artifact` default of `"document"`.
 * That mis-tag routes the body to the markdown/document renderers, which print
 * the tags as literal text (the "raw source" bug). Every client surface that
 * dispatches a renderer off `kind` — the file-preview source adapter, the
 * library tile, the chat canvas — funnels through this one guard so they agree.
 *
 * Deliberately conservative: only a leading document-level tag qualifies.
 * Genuine markdown documents don't open with `<!doctype html>` / `<html>` /
 * `<head>` / `<body>` / `<style>`, so the false-positive risk is effectively
 * nil; HTML *fragments* that markdown can legitimately embed are left alone.
 *
 * Strips one leading markdown code fence first — mirrors the server-side copy
 * in `packages/ai/src/artifacts/support/html-detect.ts`, which this function is the
 * client's independent implementation of (not a shared import; the two
 * packages don't share runtime code). Kept in sync by hand: models very
 * commonly wrap "the complete body" in a fence regardless of instructions,
 * which pushes the doctype/html tag past position 0 and made every leading-tag
 * check here miss.
 */
export function looksLikeHtmlDocument(content: string | null | undefined): boolean {
  if (!content) return false;
  const head = stripLeadingCodeFence(content.trimStart()).slice(0, 200).toLowerCase();
  return (
    head.startsWith("<!doctype html") ||
    head.startsWith("<html") ||
    head.startsWith("<head") ||
    head.startsWith("<body") ||
    head.startsWith("<style")
  );
}

/** Strips one leading ` ``` ` or ` ```<lang> ` fence line, if present. */
function stripLeadingCodeFence(content: string): string {
  const match = content.match(/^```[a-z]*\r?\n/i);
  return match ? content.slice(match[0].length) : content;
}

/**
 * The kind a `document`-tagged artifact should actually render as. Returns
 * `"html"` when the body is a full HTML document, otherwise the declared kind
 * unchanged. Central so tile / preview / canvas dispatch stays consistent.
 */
export function effectiveArtifactKind(
  kind: string,
  content: string | null | undefined,
): string {
  if (kind === "document" && looksLikeHtmlDocument(content)) return "html";
  return kind;
}
